import { TestOrgNumberButton } from '../components/TestOrgNumberButton';
import { withTestOrgNumber } from '../utils/companyIdentifiers';
import { AddressAutocomplete, applySelectedAddress } from '../components/AddressAutocomplete';
import { PhoneInput } from '../components/PhoneInput';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { DashboardLayout, Card } from '../components';
import { Breadcrumb } from '../components/Breadcrumb';
import { adminApi } from '../services/api';
import type { AdminLawyer } from '../types';
import ReactQuill from 'react-quill';
import { ProviderThemeEditor } from '../components/company-theme/ProviderThemeEditor';
import 'react-quill/dist/quill.snow.css';

interface LawyerFormData {
  name: string;
  email: string;
  lawyerCompanyName: string;
  organizationNumber: string;
  title: string;
  phone: string;
  address: string;
  postalCode: string;
  city: string;
  country: string;
  vatNumber: string;
  website: string;
  hourlyRate: string;
  experienceYears: number;
  responseTime: string;
  geographicArea: string;
  leadTime: string;
  fixedPriceAvailable: boolean;
  initialMeetingFree: boolean;
  availability: boolean;
  specialties: string;
  languages: string;
  verifiedLawyer: string;
  notes: string;
  lawyerPresentation: string;
}

function lawyerToFormData(lawyer: AdminLawyer): LawyerFormData {
  return {
    name: lawyer.name || '',
    email: lawyer.email || '',
    lawyerCompanyName: lawyer.lawyerCompanyName || '',
    organizationNumber: lawyer.organizationNumber || '',
    title: lawyer.title || '',
    phone: lawyer.phone || '',
    address: lawyer.address || '',
    postalCode: lawyer.postalCode || '',
    city: lawyer.city || '',
    country: lawyer.country || '',
    vatNumber: lawyer.vatNumber || '',
    website: lawyer.website || '',
    hourlyRate: lawyer.hourlyRate || '',
    experienceYears: lawyer.experienceYears ?? 0,
    responseTime: lawyer.responseTime || '',
    geographicArea: lawyer.geographicArea || '',
    leadTime: lawyer.leadTime || '',
    fixedPriceAvailable: lawyer.fixedPriceAvailable ?? false,
    initialMeetingFree: lawyer.initialMeetingFree ?? false,
    availability: lawyer.availability ?? false,
    specialties: (lawyer.specialties || []).join(', '),
    languages: (lawyer.languages || []).join(', '),
    verifiedLawyer: lawyer.verifiedLawyer || '',
    notes: lawyer.notes || '',
    lawyerPresentation: lawyer.lawyerPresentation || '',
  };
}

const initialFormData: LawyerFormData = {
  name: '',
  email: '',
  lawyerCompanyName: '',
  organizationNumber: '',
  title: '',
  phone: '',
  address: '',
  postalCode: '',
  city: '',
  country: '',
  vatNumber: '',
  website: '',
  hourlyRate: '',
  experienceYears: 0,
  responseTime: '',
  geographicArea: '',
  leadTime: '',
  fixedPriceAvailable: false,
  initialMeetingFree: false,
  availability: false,
  specialties: '',
  languages: '',
  verifiedLawyer: '',
  notes: '',
  lawyerPresentation: '',
};

export function AdminLawyerDetailView() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [lawyer, setLawyer] = useState<AdminLawyer | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [formData, setFormData] = useState<LawyerFormData>(initialFormData);
  const [activeTab, setActiveTab] = useState<'overview' | 'practical' | 'theme'>('overview');

  const basePath = '/admin/lawyers';

  useEffect(() => {
    if (id) {
      loadLawyer(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadLawyer = async (lawyerId: string) => {
    try {
      setIsLoading(true);
      const lawyers = await adminApi.getAllLawyers();
      const found = lawyers.find(l => l.id === lawyerId);
      if (found) {
        setLawyer(found);
        setFormData(lawyerToFormData(found));
      }
    } catch (err) {
      setMessage({ type: 'error', text: t('common.error') });
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
    setMessage(null);
  };

  const handleCancelEdit = () => {
    if (lawyer) {
      setFormData(lawyerToFormData(lawyer));
    }
    setIsEditing(false);
  };

  const handleSave = async () => {
    if (!id || !lawyer) return;
    try {
      setIsSaving(true);
      const payload = {
        ...formData,
        specialties: formData.specialties
          .split(',')
          .map(s => s.trim())
          .filter(Boolean),
        languages: formData.languages
          .split(',')
          .map(s => s.trim())
          .filter(Boolean),
      };
      const updated = await adminApi.updateLawyer(id, payload as any);
      setLawyer(updated);
      setFormData(lawyerToFormData(updated));
      setIsEditing(false);
      setMessage({ type: 'success', text: t('common.saved') });
    } catch (err) {
      setMessage({ type: 'error', text: t('common.error') });
    } finally {
      setIsSaving(false);
    }
  };

  const handleChange = (field: keyof LawyerFormData, value: string | number | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const formatDate = (dateStr: string | null | undefined): string => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr).toLocaleDateString('sv-SE');
    } catch {
      return dateStr;
    }
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (!lawyer) {
    return (
      <DashboardLayout>
        <div className="alert alert-danger">{t('common.error')}</div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      {/* Page Header */}
      <div className="page-header mb-4">
        <div className="page-header-left d-flex align-items-center">
          <div className="page-header-title">
            <h5 className="fw-bold mb-0">
              {t('adminLawyerView.title')} - {lawyer.lawyerNumber}
            </h5>
          </div>
          <Breadcrumb items={[
            { label: t('nav.home'), path: '/home' },
            { label: 'Jurister', path: basePath },
            { label: lawyer.name }
          ]} />
        </div>
        <div className="page-header-right ms-auto d-none d-sm-block">
          <p className="fw-bold text-muted mb-0">{t('adminLawyerView.description')}</p>
        </div>
        <div className="d-flex justify-content-end mt-2">
          <button className="btn btn-light-brand" onClick={() => navigate(basePath)}>
            <i className="feather-arrow-left me-2"></i>
            {t('common.back')}
          </button>
        </div>
      </div>

      {message && (
        <div className={`alert alert-${message.type === 'success' ? 'success' : 'danger'} alert-dismissible fade show`}>
          {message.text}
          <button type="button" className="btn-close" onClick={() => setMessage(null)}></button>
        </div>
      )}

      {/* Tabs Navigation */}
      <div className="mb-4" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
        <ul className="nav nav-tabs nav-tabs-custom-style flex-nowrap" role="tablist" style={{ minWidth: 'max-content' }}>
          <li className="nav-item" role="presentation">
            <button className={`nav-link px-3 ${activeTab === 'overview' ? 'active' : ''}`} onClick={() => setActiveTab('overview')}>
              <i className="feather-briefcase d-sm-none me-0"></i>
              <span className="d-none d-sm-inline">{t('adminLawyerView.tab.overview')}</span>
            </button>
          </li>
          <li className="nav-item" role="presentation">
            <button className={`nav-link px-3 ${activeTab === 'practical' ? 'active' : ''}`} onClick={() => setActiveTab('practical')}>
              <i className="feather-info d-sm-none me-0"></i>
              <span className="d-none d-sm-inline">{t('marketplace.provider.practicalInfo', 'Praktisk information')}</span>
            </button>
          </li>
          {/* Admin changes the theme on the expert's behalf; every change is logged (ClickUp 869fa6m3u). */}
          <li className="nav-item" role="presentation">
            <button className={`nav-link px-3 ${activeTab === 'theme' ? 'active' : ''}`} onClick={() => setActiveTab('theme')} data-testid="admin-provider-theme-tab">
              <i className="feather-droplet d-sm-none me-0" aria-hidden="true"></i>
              <span className="d-none d-sm-inline">{t('settings.theme.title')}</span>
              <span className="visually-hidden d-sm-none">{t('settings.theme.title')}</span>
            </button>
          </li>
        </ul>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <Card>
          <div className="card-header d-flex align-items-center justify-content-between flex-wrap gap-2">
            <h5 className="card-title mb-0">{t('adminLawyerView.tab.overview')}</h5>
            {!isEditing && (
              <button type="button" className="btn btn-primary" onClick={handleEdit} aria-label={t('common.change', 'Ändra')}>
                <i className="feather-edit me-2"></i>{t('common.change', 'Ändra')}
              </button>
            )}
          </div>
          <div className="card-body">
            {/* === Grunduppgifter === */}
            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">{t('adminLawyers.columns.lawyerNumber', 'Juristnummer')}</label>
                <input type="text" className="form-control" value={lawyer.lawyerNumber ?? ''} disabled />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">{t('lawyers.columns.verifiedLawyer', 'Status')}</label>
                {isEditing ? (
                  <select className="form-select" value={formData.verifiedLawyer} onChange={(e) => handleChange('verifiedLawyer', e.target.value)}>
                    <option value="Ej Verifierad">Ej Verifierad</option>
                    <option value="Verifierad">Verifierad</option>
                  </select>
                ) : (
                  <div><span className={`badge bg-${lawyer.verifiedLawyer === 'Verifierad' ? 'success' : 'secondary'}`}>{lawyer.verifiedLawyer || '-'}</span></div>
                )}
              </div>
            </div>

            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">{t('lawyers.columns.name', 'Namn')}</label>
                <input type="text" className="form-control" value={isEditing ? formData.name : (lawyer.name || '')} disabled={!isEditing} onChange={(e) => handleChange('name', e.target.value)} />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">{t('lawyers.columns.organizationNumber', 'Org.Nr')}</label>
                <input type="text" className="form-control" value={isEditing ? formData.organizationNumber : (lawyer.organizationNumber || '')} disabled={!isEditing} onChange={(e) => handleChange('organizationNumber', e.target.value)} />
                <TestOrgNumberButton disabled={!isEditing} onGenerated={value => setFormData(previous => withTestOrgNumber(previous, value))} />
              </div>
            </div>

            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">{t('lawyers.columns.lawyerCompanyName', 'Bolagsnamn')}</label>
                <input type="text" className="form-control" value={isEditing ? formData.lawyerCompanyName : (lawyer.lawyerCompanyName || '')} disabled={!isEditing} onChange={(e) => handleChange('lawyerCompanyName', e.target.value)} />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">{t('lawyers.columns.phone', 'Telefon')}</label>
                <PhoneInput name="phone" className="form-control" value={isEditing ? formData.phone : (lawyer.phone || '')} disabled={!isEditing} onChange={(e) => handleChange('phone', e.target.value)} />
              </div>
            </div>

            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">{t('lawyers.columns.vatNumber', 'Momsregistreringsnummer')}</label>
                <input type="text" className="form-control" value={isEditing ? formData.vatNumber : (lawyer.vatNumber || '')} disabled={!isEditing} onChange={(e) => handleChange('vatNumber', e.target.value)} />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">{t('lawyers.columns.email', 'E-post')}</label>
                <input type="email" className="form-control" value={isEditing ? formData.email : (lawyer.email || '')} disabled={!isEditing} onChange={(e) => handleChange('email', e.target.value)} />
              </div>
            </div>

            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">{t('lawyers.columns.website', 'Webbplats')}</label>
                <input type="text" className="form-control" value={isEditing ? formData.website : (lawyer.website || '')} disabled={!isEditing} onChange={(e) => handleChange('website', e.target.value)} />
              </div>
            </div>

            {/* === Adress === */}
            <hr className="my-3" />
            <h6 className="fw-bold mb-3">{t('lawyers.columns.address', 'Adress')}</h6>

            <div className="row">
              <div className="col-md-6 mb-3">
                <AddressAutocomplete
                  name="address"
                  label={t('lawyers.columns.address', 'Gatuadress')}
                  onAddressSelect={address => setFormData(previous => applySelectedAddress(previous, address))}
                  type="text"
                  value={isEditing ? formData.address : (lawyer.address || '')}
                  disabled={!isEditing}
                  onChange={(e) => handleChange('address', e.target.value)}
                />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">{t('lawyers.columns.postalCode', 'Postkod')}</label>
                <input type="text" className="form-control" value={isEditing ? formData.postalCode : (lawyer.postalCode || '')} disabled={!isEditing} onChange={(e) => handleChange('postalCode', e.target.value)} />
              </div>
            </div>

            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">{t('lawyers.columns.city', 'Stad')}</label>
                <input type="text" className="form-control" value={isEditing ? formData.city : (lawyer.city || '')} disabled={!isEditing} onChange={(e) => handleChange('city', e.target.value)} />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">{t('lawyers.columns.country', 'Land')}</label>
                <input type="text" className="form-control" value={isEditing ? formData.country : (lawyer.country || '')} disabled={!isEditing} onChange={(e) => handleChange('country', e.target.value)} />
              </div>
            </div>

            {/* === Datum === */}
            <hr className="my-3" />
            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">{t('adminLawyers.columns.createdAt', 'Skapad')}</label>
                <input type="text" className="form-control" value={formatDate(lawyer.createdAt)} disabled />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">{t('adminLawyers.columns.updatedAt', 'Uppdaterad')}</label>
                <input type="text" className="form-control" value={formatDate(lawyer.updatedAt)} disabled />
              </div>
            </div>

            {/* === Juristpresentation (rich text) === */}
            <hr className="my-3" />
            <div className="mb-3">
              <label className="form-label">{t('lawyers.columns.lawyerPresentation', 'Juristpresentation')}</label>
              {isEditing ? (
                <>
                  <ReactQuill theme="snow" value={formData.lawyerPresentation} onChange={(value) => handleChange('lawyerPresentation', value)} style={{ height: '150px' }} />
                  <div style={{ height: '45px' }}></div>
                </>
              ) : (
                lawyer.lawyerPresentation ? (
                  <div className="form-control bg-light" style={{ minHeight: '60px', height: 'auto' }}>
                    <div className="ql-editor p-0" dangerouslySetInnerHTML={{ __html: lawyer.lawyerPresentation }} />
                  </div>
                ) : (
                  <input type="text" className="form-control" value="" disabled />
                )
              )}
            </div>

            {/* === Anteckningar (rich text) === */}
            <div className="mb-3">
              <label className="form-label">{t('lawyers.columns.notes', 'Anteckningar')}</label>
              {isEditing ? (
                <>
                  <ReactQuill theme="snow" value={formData.notes} onChange={(value) => handleChange('notes', value)} style={{ height: '150px' }} />
                  <div style={{ height: '45px' }}></div>
                </>
              ) : (
                lawyer.notes ? (
                  <div className="form-control bg-light" style={{ minHeight: '60px', height: 'auto' }}>
                    <div className="ql-editor p-0" dangerouslySetInnerHTML={{ __html: lawyer.notes }} />
                  </div>
                ) : (
                  <input type="text" className="form-control" value="" disabled />
                )
              )}
            </div>

            {/* Action Buttons */}
            <div className="d-flex justify-content-end gap-2 mt-4">
              {isEditing ? (
                <>
                  <button className="btn btn-secondary" onClick={handleCancelEdit} disabled={isSaving}>
                    {t('common.cancel')}
                  </button>
                  <button className="btn btn-primary" onClick={handleSave} disabled={isSaving}>
                    {isSaving && <span className="spinner-border spinner-border-sm me-2" role="status"></span>}
                    {t('common.save')}
                  </button>
                </>
              ) : (
                <button className="btn btn-secondary" onClick={() => navigate(basePath)}>
                  {t('common.cancel')}
                </button>
              )}
            </div>
          </div>
        </Card>
      )}

      {activeTab === 'theme' && id && (
        <Card>
          <div className="card-body">
            <ProviderThemeEditor type="lawyer" userId={id} profile={lawyer ? { geographicArea: lawyer.geographicArea, languages: lawyer.languages ?? [] } : null} />
          </div>
        </Card>
      )}

      {activeTab === 'practical' && (
        <Card>
          <div className="card-header d-flex align-items-center justify-content-between flex-wrap gap-2">
            <h5 className="card-title mb-0">{t('marketplace.provider.practicalInfo', 'Praktisk information')}</h5>
            {!isEditing && (
              <button type="button" className="btn btn-primary" onClick={handleEdit} aria-label={t('common.change', 'Ändra')}>
                <i className="feather-edit me-2"></i>{t('common.change', 'Ändra')}
              </button>
            )}
          </div>
          <div className="card-body">
            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">{t('lawyers.columns.title', 'Titel')}</label>
                <input type="text" className="form-control" value={isEditing ? formData.title : (lawyer.title || '')} disabled={!isEditing} onChange={(e) => handleChange('title', e.target.value)} placeholder={t('adminLawyers.placeholderTitle')} />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">{t('lawyers.columns.experienceYears', 'Erfarenhetsår')}</label>
                <input type="number" className="form-control" value={isEditing ? formData.experienceYears : (lawyer.experienceYears ?? 0)} disabled={!isEditing} onChange={(e) => handleChange('experienceYears', parseInt(e.target.value, 10) || 0)} />
              </div>
            </div>

            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">{t('lawyers.columns.specialties', 'Specialistområden')}</label>
                {isEditing ? (
                  <>
                    <input type="text" className="form-control" value={formData.specialties} onChange={(e) => handleChange('specialties', e.target.value)} placeholder="Avtalsrätt, Entreprenadjuridik, Tvistlösning" />
                    <small className="text-muted">{t('common.commaSeparated', 'Kommaseparerade värden')}</small>
                  </>
                ) : (
                  <div>{(lawyer.specialties || []).length > 0 ? (lawyer.specialties || []).map((s, i) => <span key={i} className="badge bg-primary me-1 mb-1">{s}</span>) : <span className="text-muted">-</span>}</div>
                )}
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">{t('lawyers.columns.languages', 'Språk')}</label>
                {isEditing ? (
                  <>
                    <input type="text" className="form-control" value={formData.languages} onChange={(e) => handleChange('languages', e.target.value)} placeholder="Svenska, Engelska" />
                    <small className="text-muted">{t('common.commaSeparated', 'Kommaseparerade värden')}</small>
                  </>
                ) : (
                  <div>{(lawyer.languages || []).length > 0 ? (lawyer.languages || []).map((l, i) => <span key={i} className="badge bg-info me-1 mb-1">{l}</span>) : <span className="text-muted">-</span>}</div>
                )}
              </div>
            </div>

            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">{t('lawyers.columns.hourlyRate', 'Timarvode')}</label>
                <input type="text" className="form-control" value={isEditing ? formData.hourlyRate : (lawyer.hourlyRate || '')} disabled={!isEditing} onChange={(e) => handleChange('hourlyRate', e.target.value)} placeholder="t.ex. 2 500 kr/h" />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">{t('lawyers.columns.responseTime', 'Svarstid')}</label>
                <input type="text" className="form-control" value={isEditing ? formData.responseTime : (lawyer.responseTime || '')} disabled={!isEditing} onChange={(e) => handleChange('responseTime', e.target.value)} placeholder="t.ex. Inom 24h" />
              </div>
            </div>

            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">{t('lawyers.columns.geographicArea', 'Geografiskt område')}</label>
                <input type="text" className="form-control" value={isEditing ? formData.geographicArea : (lawyer.geographicArea || '')} disabled={!isEditing} onChange={(e) => handleChange('geographicArea', e.target.value)} placeholder="t.ex. Stockholm, Mälardalen" />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">{t('lawyers.columns.leadTime', 'Ledtid')}</label>
                <input type="text" className="form-control" value={isEditing ? formData.leadTime : (lawyer.leadTime || '')} disabled={!isEditing} onChange={(e) => handleChange('leadTime', e.target.value)} placeholder="t.ex. 1-2 veckor" />
              </div>
            </div>

            <div className="row">
              <div className="col-md-4 mb-3">
                <label className="form-label">{t('lawyers.columns.fixedPriceAvailable', 'Fast pris möjligt')}</label>
                {isEditing ? (
                  <div className="form-check mt-2">
                    <input type="checkbox" className="form-check-input" id="fixedPriceAvailable" checked={formData.fixedPriceAvailable} onChange={(e) => handleChange('fixedPriceAvailable', e.target.checked)} />
                    <label className="form-check-label" htmlFor="fixedPriceAvailable">{formData.fixedPriceAvailable ? 'Ja' : 'Nej'}</label>
                  </div>
                ) : (
                  <div><span className={`badge bg-${lawyer.fixedPriceAvailable ? 'success' : 'secondary'}`}>{lawyer.fixedPriceAvailable ? 'Ja' : 'Nej'}</span></div>
                )}
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label">{t('lawyers.columns.initialMeetingFree', 'Gratis inledande möte')}</label>
                {isEditing ? (
                  <div className="form-check mt-2">
                    <input type="checkbox" className="form-check-input" id="initialMeetingFree" checked={formData.initialMeetingFree} onChange={(e) => handleChange('initialMeetingFree', e.target.checked)} />
                    <label className="form-check-label" htmlFor="initialMeetingFree">{formData.initialMeetingFree ? 'Ja' : 'Nej'}</label>
                  </div>
                ) : (
                  <div><span className={`badge bg-${lawyer.initialMeetingFree ? 'success' : 'secondary'}`}>{lawyer.initialMeetingFree ? 'Ja' : 'Nej'}</span></div>
                )}
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label">{t('lawyers.columns.availability', 'Tillgänglig för nya uppdrag')}</label>
                {isEditing ? (
                  <div className="form-check mt-2">
                    <input type="checkbox" className="form-check-input" id="availability" checked={formData.availability} onChange={(e) => handleChange('availability', e.target.checked)} />
                    <label className="form-check-label" htmlFor="availability">{formData.availability ? 'Ja' : 'Nej'}</label>
                  </div>
                ) : (
                  <div><span className={`badge bg-${lawyer.availability ? 'success' : 'secondary'}`}>{lawyer.availability ? 'Ja' : 'Nej'}</span></div>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="d-flex justify-content-end gap-2 mt-4">
              {isEditing ? (
                <>
                  <button className="btn btn-secondary" onClick={handleCancelEdit} disabled={isSaving}>
                    {t('common.cancel')}
                  </button>
                  <button className="btn btn-primary" onClick={handleSave} disabled={isSaving}>
                    {isSaving && <span className="spinner-border spinner-border-sm me-2" role="status"></span>}
                    {t('common.save')}
                  </button>
                </>
              ) : (
                <button className="btn btn-secondary" onClick={() => navigate(basePath)}>
                  {t('common.cancel')}
                </button>
              )}
            </div>
          </div>
        </Card>
      )}
    </DashboardLayout>
  );
}
